import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

// Use environment variable for backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});  // {module: ["ver","crear",...]}
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  // Iter 200 (P0) — Mapeo ruta→módulo cargado del backend (fuente única de verdad)
  const [routeMapping, setRouteMapping] = useState(null);  // null = aún cargando
  const tokenRef = useRef(token);

  // Keep ref in sync
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Stable axios instance with interceptor
  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const t = tokenRef.current;
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && tokenRef.current) {
          localStorage.removeItem('token');
          tokenRef.current = null;
          setToken(null);
          setUser(null);
          setPermissions({});
          setIsSuperAdmin(false);
          setPermissionsLoaded(false);
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, []);

  // axiosAuth returns the stable instance (backwards compatible)
  const axiosAuth = useMemo(() => () => api, [api]);

  const loadPermissions = async () => {
    try {
      const res = await api.get('/auth/me/permissions');
      setPermissions(res.data.permissions || {});
      setIsSuperAdmin(!!res.data.is_super_admin);
    } catch {
      setPermissions({});
      setIsSuperAdmin(false);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  // Iter 200 (P0) — Carga el mapping ruta→módulo desde db.system_modules.
  // Es la fuente única de verdad para los guards de ruta (RequireModule).
  const loadRouteMapping = async () => {
    try {
      const res = await api.get('/system/route-mapping');
      setRouteMapping(res.data?.mapping || {});
    } catch {
      // Fallback vacío — RequireModule usará el mapeo estático interno.
      setRouteMapping({});
    }
  };

  useEffect(() => {
    if (token) {
      api.get('/auth/me')
        .then(async res => {
          setUser(res.data);
          await Promise.all([loadPermissions(), loadRouteMapping()]);
          setLoading(false);
        })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
      setPermissionsLoaded(true);
      setRouteMapping({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, must_change_password, password_change_warning, password_change_deadline } = res.data;
    localStorage.setItem('token', newToken);
    tokenRef.current = newToken;
    setToken(newToken);
    // Adjuntar flags de política en el objeto user para que la UI los lea.
    const enriched = {
      ...userData,
      must_change_password: !!must_change_password,
      password_change_warning: !!password_change_warning,
      password_change_deadline: password_change_deadline || "",
    };
    setUser(enriched);
    setPermissionsLoaded(false);
    await Promise.all([loadPermissions(), loadRouteMapping()]);
    return enriched;
  };

  /** Refresca user desde /auth/me — útil tras cambiar contraseña. */
  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    setPermissions({});
    setIsSuperAdmin(false);
    setPermissionsLoaded(false);
    // Iter 153 — limpiar caché del hook useModulePermissions
    try {
      const mod = require("@/hooks/useModulePermissions");
      if (mod && typeof mod.clearPermissionsCache === "function") {
        mod.clearPermissionsCache();
      }
    } catch { /* ignore */ }
  };

  /**
   * Comprueba si el usuario actual tiene un permiso concreto sobre un módulo.
   * super_admin pasa siempre. El nivel del rol NO concede acceso automático.
   */
  const hasPermission = (module, action = "ver") => {
    if (isSuperAdmin) return true;
    const acts = permissions?.[module];
    if (!Array.isArray(acts)) return false;
    return acts.includes(action) || acts.includes("todo");
  };

  const refreshPermissions = async () => {
    setPermissionsLoaded(false);
    await loadPermissions();
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, axiosAuth, API, api,
      permissions, isSuperAdmin, permissionsLoaded,
      routeMapping,
      hasPermission, refreshPermissions, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
